/**
 * confirmDialog — imperative confirm-from-anywhere primitive.
 *
 * Mirrors the toast pattern (`hooks/use-toast.ts`): a declarative React
 * component (`ConfirmDialogHost`) is mounted once near the app root, and
 * any non-React caller can request a confirmation by `await`ing
 * `confirmDialog({ ... })`. The Promise resolves `true` on confirm and
 * `false` on cancel / Esc / backdrop click — the same boolean shape the
 * MCP-UI cross-origin link gate (T3.7) needs.
 *
 * Design notes:
 *   - A Zustand store holds the active request and a FIFO queue. Two
 *     concurrent `confirmDialog` calls don't drop the second — they
 *     queue and resolve in order.
 *   - The store keeps each request's `resolve` function alongside its
 *     props. The host component only renders `active`; on close it
 *     resolves and promotes the next queued entry.
 *   - "Resolve once" guard: Radix fires `onOpenChange(false)` for Esc /
 *     backdrop / X-close button. If Radix or React StrictMode ever
 *     double-fires the close callback for a single user gesture, the
 *     guard makes the second call a no-op rather than cancelling the
 *     newly-promoted request.
 *   - The Confirm and Cancel buttons are plain shadcn `Button`s (not
 *     Radix `DialogClose`), so clicking them does NOT trigger
 *     `onOpenChange` — they call `resolveActive` directly. This split
 *     is intentional: it keeps the resolution boolean symmetric (both
 *     buttons hit the same code path with different values).
 */

import { useEffect } from 'react'
import { create } from 'zustand'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'

export interface ConfirmDialogOptions {
  title: string
  body: string
  confirmLabel?: string
  cancelLabel?: string
}

interface ConfirmRequest extends ConfirmDialogOptions {
  resolve: ((value: boolean) => void) | null
}

interface ConfirmDialogState {
  active: ConfirmRequest | null
  queue: ConfirmRequest[]
  enqueue: (req: ConfirmRequest) => void
  resolveActive: (value: boolean) => void
  /**
   * Drain the entire store: resolve the active request AND every
   * queued request as cancel, then clear. Used by `ConfirmDialogHost`
   * on unmount so no awaiting caller is left hanging — a single
   * `resolveActive(false)` would only resolve the active item and
   * promote the next, leaving the promotion (and any remaining queue)
   * stranded with nothing to render them.
   */
  drainAll: () => void
}

const useConfirmDialogStore = create<ConfirmDialogState>((set, get) => ({
  active: null,
  queue: [],
  enqueue: (req) => {
    const { active } = get()
    if (active === null) {
      set({ active: req })
    } else {
      set((s) => ({ queue: [...s.queue, req] }))
    }
  },
  resolveActive: (value) => {
    const { active, queue } = get()
    if (active === null) return
    const resolver = active.resolve
    // Mark the active request as resolved BEFORE calling the resolver
    // so a re-entrant `onOpenChange(false)` (e.g. Radix double-fire,
    // StrictMode) becomes a no-op for the same object. Use an
    // immutable Zustand update so this guard survives any future
    // immer/spread middleware — direct object mutation would be
    // invisible to subsequent `get()` reads under those refactors.
    set((s) =>
      s.active === active ? { active: { ...active, resolve: null } } : {},
    )
    if (resolver !== null) resolver(value)
    if (queue.length === 0) {
      set({ active: null })
    } else {
      const [next, ...rest] = queue
      // queue.length > 0 guarantees `next` is defined; the assertion
      // is a TypeScript artifact of array-destructuring widening.
      set({ active: next!, queue: rest })
    }
  },
  drainAll: () => {
    const { active, queue } = get()
    if (active !== null && active.resolve !== null) active.resolve(false)
    for (const req of queue) {
      if (req.resolve !== null) req.resolve(false)
    }
    set({ active: null, queue: [] })
  },
}))

/**
 * Request a confirmation from the user. Returns true if confirmed,
 * false on cancel / Esc / backdrop click.
 *
 * `<ConfirmDialogHost />` MUST be mounted near the app root for this
 * Promise to resolve.
 */
export function confirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    useConfirmDialogStore.getState().enqueue({ ...options, resolve })
  })
}

export function ConfirmDialogHost() {
  const active = useConfirmDialogStore((s) => s.active)
  const resolveActive = useConfirmDialogStore((s) => s.resolveActive)

  // Defensive cleanup: if the host unmounts while requests are
  // pending, drain the entire store as cancel so no awaiting caller
  // is left hanging. In practice the host lives at the app root for
  // the app's lifetime, but the contract should not depend on that.
  useEffect(() => {
    return () => {
      useConfirmDialogStore.getState().drainAll()
    }
  }, [])

  const open = active !== null
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Radix sends `false` for Esc / overlay click / explicit X
        // close button. All three should resolve as cancel. Confirm/
        // Cancel buttons bypass this — they're plain shadcn Buttons
        // and resolve directly via their onClick handlers.
        if (!next) resolveActive(false)
      }}
    >
      <DialogContent>
        {active !== null && (
          <>
            <DialogHeader>
              <DialogTitle>{active.title}</DialogTitle>
              <DialogDescription>{active.body}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => resolveActive(false)}
              >
                {active.cancelLabel ?? 'Cancel'}
              </Button>
              <Button onClick={() => resolveActive(true)}>
                {active.confirmLabel ?? 'Confirm'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** Test-only: drain the store so cases don't leak state between tests. */
export function __resetConfirmDialogForTests() {
  useConfirmDialogStore.setState({ active: null, queue: [] })
}
